import React from 'react';

import { LUMIO_MOBILE_ANIM } from '../../../../theme/tokens';
// Glow colours come from tokens.raw (RAW hex): they are baked into a data-URI SVG
// (waveTileImage), and var(--ows-…) theme tokens do NOT resolve inside a data URI.
import { LUMIO_MOBILE as LUMIO_RAW } from '../../../../theme/tokens.raw';
import { BOTTOM_WAVE_TILES, waveTileImage } from '../../../../layouts/MainLayout/components/LumioMobile/waves';
import { Star, StarField, WaveBand, WaveLayer } from './BusinessValueWaves.styles';

/**
 * The Lumio-style backdrop for the Pulse "Business Value" hero — the same premium
 * treatment as the Home "Important Now" Lumio card (LumioWaves), tuned for this wide
 * "king of stage" card. Two purely-decorative (aria-hidden) layers sit behind the
 * figure + chart:
 *  - glow waves: the mobile-overlay wave GEOMETRY (`waves.ts`) + brand glow colours,
 *    three blurred parallax layers screen-blending up from the bottom — so the violet
 *    value line reads as the bright crest riding on top of the glow. Each layer is a
 *    TILED data-URI wave drifted via `background-position` (NOT a 400%-wide transform
 *    layer — that flickers on Safari, see waves.ts / BusinessValueWaves.styles);
 *  - shooting stars: faint cyan→white streaks darting up-right across the chart.
 * Both honour `prefers-reduced-motion` (waves hold a static frame; stars hide).
 */
type LayerDef = {
  key: string;
  img: string;
  durationMs: number;
  reverse?: boolean;
  blur: string;
  opacity: number;
  delayMs?: number;
};

/** Drift speed-up over the slow ambient mobile-overlay tokens. The "generating"
 *  banner runs 6× (frantic — it signals active work); this permanent hero card uses a
 *  calmer 1.5× so the glow feels alive and premium, not busy. Applied locally so the
 *  shared LUMIO_MOBILE_ANIM tokens (and the mobile overlay) are untouched. */
const WAVE_SPEEDUP = 1.5;

/** How tall the glow-wave band is, as a share of the card height. '100%' makes the
 *  waves as tall as the card; lower values keep the glow hugging the foot. */
const WAVE_BAND_HEIGHT = '200%';

const LAYERS: LayerDef[] = [
  {
    key: 'violet',
    img: waveTileImage(BOTTOM_WAVE_TILES.violet, LUMIO_RAW.glowViolet, 'down'),
    durationMs: LUMIO_MOBILE_ANIM.waveSlowMs / WAVE_SPEEDUP,
    reverse: true,
    blur: LUMIO_MOBILE_ANIM.glowBlurSoft,
    // Dialed to ~15% of the banner's opacity (a 75% cut, then a further 40%) so the
    // glow reads as a faint wash behind this hero card rather than a bold bloom.
    opacity: 0.09,
    delayMs: 4000,
  },
  {
    key: 'magenta',
    img: waveTileImage(BOTTOM_WAVE_TILES.magenta, LUMIO_RAW.glowMagenta, 'down'),
    durationMs: LUMIO_MOBILE_ANIM.waveMidMs / WAVE_SPEEDUP,
    blur: LUMIO_MOBILE_ANIM.glowBlurSoft,
    opacity: 0.075,
    delayMs: 7000,
  },
  {
    key: 'cyan',
    img: waveTileImage(BOTTOM_WAVE_TILES.cyan, LUMIO_RAW.glowCyan, 'down'),
    durationMs: LUMIO_MOBILE_ANIM.waveFastMs / WAVE_SPEEDUP,
    reverse: true,
    blur: LUMIO_MOBILE_ANIM.glowBlurSoft,
    opacity: 0.125,
    delayMs: 12000,
  },
];

const Layer = ({ def, intensity }: { def: LayerDef; intensity: number }): React.ReactElement => (
  <WaveLayer
    $img={def.img}
    $dur={def.durationMs}
    $reverse={def.reverse}
    $blur={def.blur}
    $opacity={Math.min(1, def.opacity * intensity)}
    $delay={def.delayMs}
  />
);

/** Pulse-only tuning applied over the base streak cadence below: 50% longer tails,
 *  and 30% slower (speed × 0.7 ⇒ duration ÷ 0.7 ≈ 1.43×) for a calmer, more graceful
 *  shoot. Kept as multipliers so the base `STARS` values stay the readable design. */
const STAR_TAIL_SCALE = 1.5;
const STAR_SLOWDOWN = 1 / 0.7;

/** Streaks across the chart side: varied start, length, speed, stagger + faintness
 *  so they read as occasional shooting stars rather than a steady stream. */
type StarDef = { key: string; top: string; left: string; len: number; durMs: number; delayMs: number; peak: number };
const STARS: StarDef[] = [
  { key: 's1', top: '70%', left: '4%', len: 64, durMs: 5200, delayMs: 0, peak: 0.42 },
  { key: 's2', top: '86%', left: '22%', len: 46, durMs: 6400, delayMs: 1800, peak: 0.3 },
  { key: 's3', top: '54%', left: '40%', len: 72, durMs: 4600, delayMs: 3200, peak: 0.38 },
  { key: 's4', top: '92%', left: '12%', len: 40, durMs: 7000, delayMs: 4600, peak: 0.26 },
  { key: 's5', top: '76%', left: '58%', len: 56, durMs: 5600, delayMs: 2500, peak: 0.34 },
  { key: 's6', top: '60%', left: '78%', len: 50, durMs: 6000, delayMs: 5200, peak: 0.3 },
];

export type BusinessValueWavesProps = {
  /** Multiplies the glow-layer opacities, the light-theme band wash, AND the shooting-star
   *  peak brightness, so the whole effect lifts together. 1 = the Business Value hero's
   *  calibrated faint wash; the Pulse "Calibrating" card passes 1.5 for 50% more vivid.
   *  Each value is still clamped to a max of 1. */
  intensity?: number;
  /** StarField bounding box (`inset` shorthand). Defaults to the hero's right-side band
   *  (`0 0 0 34%`); a centred card (no chart column) passes `0` to span the whole card. */
  starInset?: string;
  /** Render the shooting-star accent. On by default (the Business Value hero); the Pulse
   *  "Calibrating" panel passes `false` to keep only the glow waves. */
  stars?: boolean;
};

export const BusinessValueWaves = ({ intensity = 1, starInset = '0 0 0 34%', stars = true }: BusinessValueWavesProps = {}): React.ReactElement => (
  <>
    <WaveBand aria-hidden="true" $height={WAVE_BAND_HEIGHT} $intensity={intensity}>
      {LAYERS.map((def) => (
        <Layer key={def.key} def={def} intensity={intensity} />
      ))}
    </WaveBand>
    {stars ? (
      <StarField aria-hidden="true" $inset={starInset}>
        {STARS.map((s) => (
          <Star
            key={s.key}
            $top={s.top}
            $left={s.left}
            $len={Math.round(s.len * STAR_TAIL_SCALE)}
            $dur={Math.round(s.durMs * STAR_SLOWDOWN)}
            $delay={s.delayMs}
            $peak={Math.min(1, s.peak * intensity)}
          />
        ))}
      </StarField>
    ) : null}
  </>
);

export default BusinessValueWaves;
